import { Component, DestroyRef, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { InstanceIdService } from '@shared/services/instance-id.service';
import { SubscriptionManager } from '@shared/services/subscription-manager.service';
import moment from 'moment';
import { Subject } from 'rxjs';
import { ErrorService } from 'src/app/shared/services/error.service';
import { ToastService } from 'src/app/shared/services/toast.service';

@Component({
  selector: 'app-popup-export-staff',
  templateUrl: './popup-export-staff.component.html',
  styleUrls: ['./popup-export-staff.component.scss'],
  standalone: false,
})
export class PopupExportStaffComponent implements OnInit, OnDestroy {
  private instanceId: string;

  private destroy$ = new Subject<void>();

  public formGroup: FormGroup | undefined;

  public isExporting: boolean = false;

  public isRecoveryRoomExport: boolean = false;

  public isAutofilling: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<PopupExportStaffComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    private errorService: ErrorService,
    private subscriptionManager: SubscriptionManager,
    private instanceIdService: InstanceIdService,
    private destroyRef: DestroyRef
  ) {
    this.instanceId = this.instanceIdService.generateId();
    this.subscriptionManager.registerComponent(this.instanceId, this.destroyRef);
  }

  ngOnInit(): void {
    const start = this.data?.startDate ? new Date(this.data.startDate) : new Date();
    const end = this.data?.endDate ? new Date(this.data.endDate) : new Date();
    this.isAutofilling = this.data?.isAutofilling;
    this.isRecoveryRoomExport = this.data?.isRecoveryRoomExport;
    this.formGroup = this.formBuilder.group(
      {
        startDate: [this.UTCDateToDate(start), [Validators.required]],
        endDate: [this.UTCDateToDate(end), [Validators.required]],
      },
      { validators: [this.dateRangeValidator] }
    );
  }

  private dateRangeValidator(group: AbstractControl): ValidationErrors | null {
    const start: Date | null = group.get('startDate')?.value ?? null;
    const end: Date | null = group.get('endDate')?.value ?? null;

    if (!start || !end) {
      return null;
    }

    const startDay = new Date(start);
    startDay.setHours(0, 0, 0, 0);

    const endDay = new Date(end);
    endDay.setHours(0, 0, 0, 0);

    return endDay.getTime() < startDay.getTime() ? { invalidRange: true } : null;
  }

  dateChange(event: { startDate: Date; endDate: Date }) {
    if (this.formGroup) {
      this.formGroup.get('startDate')?.setValue(new Date(event.startDate));
      this.formGroup.get('endDate')?.setValue(new Date(event.endDate));
    }
  }

  export(): void {
    try {
      if (!this.formGroup || this.formGroup.invalid) {
        this.formGroup?.markAllAsTouched();
        return;
      }

      const startDate = moment(this.formGroup.controls['startDate'].value).format('YYYY-MM-DD');
      const endDate = moment(this.formGroup.controls['endDate'].value).format('YYYY-MM-DD');

      this.isExporting = true;

      this.dialogRef.close({
        startDate,
        endDate,
      });
    } catch (error) {
      this.errorService.handleError(error);
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  private UTCDateToDate(date: Date): Date {
    return new Date(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds()
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (!this.isExporting) {
      this.toastService.errorToast('Annulation');
    }
  }
}
